import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';
import { QueryOptions } from '@apollo/client/core';

import { GetMostReadArticles } from '@root/modules/headlines/types/headlines';
import getMostReadArticles from '@root/modules/headlines/graphql/getMostReadArticles.graphql';
import { dataLoader } from '@root/libs/redis/dataloader/dataLoader';

type ServiceVariables = GetMostReadArticles['variables'];

type FetchVariables = GetMostReadArticles['variables'];

type Response = GetMostReadArticles['data'];

const emptyHeadlines = {
  items: [],
  pager: {
    offset: 0,
    count: 0,
  },
};

export default class MostReadArticlesService extends Service implements ServiceInterface {
  private async fetchMostReadArticles(variables: FetchVariables) {
    try {
      const options = Object.assign({ query: getMostReadArticles }, { variables });
      const dataLoaderOptions = {
        remote: {
          keyPrefix: 'mostread_headlines',
        },
        cacheKeyFn: (params: Record<string, unknown>) => {
          const key = JSON.stringify(params.variables);
          return key;
        },
      };

      const requestWrapper = (options: QueryOptions) => {
        const apiProvider = this.createProvider('GraphQL');
        apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });
        return apiProvider.request<Response>(options);
      };
      const redisDataLoader = dataLoader<QueryOptions, Response>(requestWrapper, dataLoaderOptions);
      const response: Response = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

      if (!response?.headlines?.items.length) {
        throw 'Empty headlines';
      }

      return {
        items: response?.headlines?.items || [],
        pager: response?.headlines?.pager,
      };
    } catch {
      return emptyHeadlines;
    }
  }

  public async fetch(variables: ServiceVariables) {
    const isHealthy = this.getHealthCheckStatus('content-api-v3');

    if (!isHealthy) {
      return {
        items: [],
        pager: {
          offset: 0,
          count: 0,
        },
      };
    }

    const response = await this.fetchMostReadArticles(variables);

    return response;
  }
}
